import { useState } from 'react'
import { useLang } from '../../i18n/LanguageContext'

function getPasswordStrength(password) {
  if (!password) return 0
  let score = 0
  if (password.length >= 8) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++
  return score
}

export default function Step4Account({ data, onChange, onNext, onBack }) {
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useLang()

  const strength = getPasswordStrength(data.ownerPassword || '')

  const validate = () => {
    const e = {}
    const ownerUsername = data.ownerUsername?.trim() || ''
    const isEmailUsername = ownerUsername.includes('@')

    if (!ownerUsername) e.ownerUsername = t('onboarding.usernameRequired')
    else if (ownerUsername.length < 3) e.ownerUsername = t('onboarding.usernameMin')
    else if (ownerUsername.length > 256) e.ownerUsername = t('onboarding.usernameMax')
    else if (isEmailUsername && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerUsername))
      e.ownerUsername = t('onboarding.usernameEmailInvalid')
    else if (!isEmailUsername && !/^[a-zA-Z0-9._-]+$/.test(ownerUsername))
      e.ownerUsername = t('onboarding.usernameFormat')

    if (!data.ownerPassword) e.ownerPassword = t('onboarding.passwordRequired')
    else if (data.ownerPassword.length < 8) e.ownerPassword = t('onboarding.passwordMin')

    if (data.ownerPassword !== data.ownerPasswordConfirm)
      e.ownerPasswordConfirm = t('onboarding.passwordMismatch')

    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleNext = () => {
    if (validate()) onNext()
  }

  return (
    <div className="wizard__body">
      <h1 className="wizard__title">{t('onboarding.step4Title')}</h1>
      <p className="wizard__subtitle">
        {t('onboarding.step4Subtitle')}
      </p>

      <div className="form-group">
        <label className="form-label form-label--required">{t('onboarding.username')}</label>
        <input
          className={`form-input ${errors.ownerUsername ? 'form-input--error' : ''}`}
          type="text"
          placeholder={t('onboarding.usernamePlaceholder')}
          autoComplete="username"
          value={data.ownerUsername || ''}
          onChange={(e) => onChange({ ownerUsername: e.target.value })}
          maxLength={256}
          autoFocus
        />
        {errors.ownerUsername && <div className="form-error">{errors.ownerUsername}</div>}
        <div className="form-hint">{t('onboarding.usernameHint')}</div>
      </div>

      <div className="form-group">
        <label className="form-label form-label--required">{t('onboarding.loginEmail')}</label>
        <input
          className="form-input"
          type="email"
          value={data.contactEmail || ''}
          disabled
          style={{ background: 'var(--color-gray-50)', color: 'var(--color-gray-500)' }}
        />
        <div className="form-hint">{t('onboarding.loginEmailHint')}</div>
      </div>

      <div className="form-group">
        <label className="form-label form-label--required">{t('onboarding.password')}</label>
        <div style={{ position: 'relative' }}>
          <input
            className={`form-input ${errors.ownerPassword ? 'form-input--error' : ''}`}
            type={showPassword ? 'text' : 'password'}
            placeholder={t('onboarding.passwordMin')}
            autoComplete="new-password"
            value={data.ownerPassword || ''}
            onChange={(e) => onChange({ ownerPassword: e.target.value })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)'
            }}
          >
            {showPassword ? t('onboarding.passwordHide') : t('onboarding.passwordShow')}
          </button>
        </div>
        {errors.ownerPassword && <div className="form-error">{errors.ownerPassword}</div>}
        {data.ownerPassword && (
          <div className="password-strength">
            {[1, 2, 3, 4].map(i => (
              <div
                key={i}
                className={`password-strength__bar ${i <= strength ? `password-strength__bar--filled-${strength}` : ''}`}
              />
            ))}
          </div>
        )}
        <div className="form-hint">
          {t('onboarding.passwordHint')}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label form-label--required">{t('onboarding.passwordConfirm')}</label>
        <input
          className={`form-input ${errors.ownerPasswordConfirm ? 'form-input--error' : ''}`}
          type={showPassword ? 'text' : 'password'}
          placeholder={t('onboarding.passwordConfirmPlaceholder')}
          autoComplete="new-password"
          value={data.ownerPasswordConfirm || ''}
          onChange={(e) => onChange({ ownerPasswordConfirm: e.target.value })}
        />
        {errors.ownerPasswordConfirm && <div className="form-error">{errors.ownerPasswordConfirm}</div>}
      </div>

      <div className="wizard__actions">
        <button className="btn btn--secondary" onClick={onBack}>
          {t('onboarding.back')}
        </button>
        <button className="btn btn--primary" onClick={handleNext}>
          {t('onboarding.next')}
        </button>
      </div>
    </div>
  )
}
